import { useMemo } from 'react';
import { Button, Checkbox, Modal, Space, Tooltip, Typography } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import type { ReportColumn } from '@goelectric/shared-types';

/** Una columna declarada con su estado de selección, en el orden de salida. */
export interface ColumnSelection {
  clave: string;
  seleccionada: boolean;
}

export interface ReportColumnsModalProps {
  open: boolean;
  /** Columnas declaradas por la definición; fijan títulos y orden de salida. */
  columnas: ReportColumn[];
  value: ColumnSelection[];
  onChange: (next: ColumnSelection[]) => void;
  onClose: () => void;
}

/** Estado inicial: todas las columnas seleccionadas, en el orden declarado. */
export function defaultColumnSelection(columnas: ReportColumn[]): ColumnSelection[] {
  return columnas.map((columna) => ({ clave: columna.clave, seleccionada: true }));
}

/**
 * Panel de columnas del reporte. Se abre desde el menú "Descargar". El orden que
 * se emite es SIEMPRE el declarado en la definición: la UI sólo marca y desmarca.
 */
export function ReportColumnsModal({
  open,
  columnas,
  value,
  onChange,
  onClose,
}: ReportColumnsModalProps) {
  const seleccion = useMemo(
    () => new Map(value.map((columna) => [columna.clave, columna.seleccionada] as const)),
    [value],
  );

  const estaSeleccionada = (clave: string) => seleccion.get(clave) ?? true;
  const seleccionadas = columnas.filter((columna) => estaSeleccionada(columna.clave)).length;

  /** Arma la selección en el orden declarado, ignorando el orden del `value`. */
  const build = (pick: (clave: string) => boolean): ColumnSelection[] =>
    columnas.map((columna) => ({ clave: columna.clave, seleccionada: pick(columna.clave) }));

  const toggle = (clave: string, checked: boolean) =>
    onChange(build((actual) => (actual === clave ? checked : estaSeleccionada(actual))));

  return (
    <Modal
      open={open}
      title="Elegir columnas"
      width={860}
      onCancel={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          Listo
        </Button>
      }
    >
      <div className="reportes-columns__toolbar">
        <Typography.Text type="secondary">
          {seleccionadas} de {columnas.length} columnas
        </Typography.Text>
        <Space wrap>
          <Button size="small" onClick={() => onChange(build(() => true))}>
            Todas
          </Button>
          <Button size="small" onClick={() => onChange(build(() => false))}>
            Ninguna
          </Button>
          <Button size="small" onClick={() => onChange(defaultColumnSelection(columnas))}>
            Restablecer
          </Button>
        </Space>
      </div>

      <div className="reportes-columns__grid">
        {columnas.map((columna) => (
          <Checkbox
            key={columna.clave}
            checked={estaSeleccionada(columna.clave)}
            onChange={(event) => toggle(columna.clave, event.target.checked)}
          >
            {columna.titulo}
            {columna.filtro && (
              <Tooltip title="Esta columna agrega un filtro al reporte">
                <FilterOutlined
                  className="reportes-columns__filter-icon"
                  aria-label="Columna filtrable"
                />
              </Tooltip>
            )}
          </Checkbox>
        ))}
      </div>

      {seleccionadas === 0 && (
        <Typography.Text type="warning" className="reportes-columns__warning">
          Marcá al menos una columna para ejecutar y descargar el reporte.
        </Typography.Text>
      )}
    </Modal>
  );
}
